import { NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseHorizontalNavigationComponent, FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { FooterType01Component } from 'app/layout/common/footer/footer-type01/footer-type01.component';
import { FooterType02Component } from 'app/layout/common/footer/footer-type02/footer-type02.component';
import { FooterType03Component } from 'app/layout/common/footer/footer-type03/footer-type03.component';
import { FooterComponent } from 'app/layout/common/footer/footer.component';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { TFMCartService } from 'app/mock-api/tfm-cart.service';
import { TFMService } from 'app/mock-api/tfm.service';
import { Subject, takeUntil } from 'rxjs';
import { FooterType04Component } from "../../../common/footer/footer-type04/footer-type04.component";
import { CartComponent } from 'app/layout/common/cart/cart.component';
import { FooterType05Component } from "../../../common/footer/footer-type05/footer-type05.component";
import { FooterType06Component } from "../../../common/footer/footer-type06/footer-type06.component";
import { FooterType07Component } from "../../../common/footer/footer-type07/footer-type07.component";

@Component({
    selector: 'modern-layout',
    templateUrl: './modern.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [FuseLoadingBarComponent, NgIf, FuseVerticalNavigationComponent, FuseHorizontalNavigationComponent, MatButtonModule, MatIconModule,
    LanguagesComponent, FuseFullscreenComponent, SearchComponent, ShortcutsComponent, MessagesComponent, NotificationsComponent, UserComponent, RouterOutlet,
    QuickChatComponent, FooterType01Component, FooterType02Component, FooterComponent, FooterType03Component, FooterType04Component, CartComponent, FooterType05Component, FooterType06Component, FooterType07Component],
})
export class ModernLayoutComponent implements OnInit, OnDestroy {
    public view: any = {
        isClient: false,
        data: {
            cart: []
        },
        drawers: {
            review: {}
        }
    };
    public drawerMode: 'over' | 'side' | 'push' = 'push';

    isScreenSmall: boolean;
    navigation: Navigation;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private tfmService: TFMService,
        private _tfmCartService: TFMCartService,
        private _cd: ChangeDetectorRef

    ) {
        this.view.tripChange$ = this._tfmCartService.tripChange.subscribe(res => {
            this.view.trip = res;
        });

        this.view.cartChange$ = this._tfmCartService.cartChange.subscribe(res => {
            // console.log("cart", res);
            this.view.data.cart = res;
            this._cd.detectChanges();
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    sendEmail(email: string) {
        const subject = `Website Contact Request`;
        const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}`;
        window.location.href = mailtoLink;
    }

    /** Whether the current route is the search result screen (cart should show limited columns). */
    isSearchResultRoute(): boolean {
        return this._router.url.includes('search-results');
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * Build a flat list of nav items for Destinations: category headers (disabled) plus destination links,
     * so the menu expands downward in one dropdown instead of flyouts to the right.
     */
    private _buildDestinationsFlat(sortedContinents: string[], grouped: Record<string, any[]>): any[] {
        const flat: any[] = [];
        for (const continent of sortedContinents) {
            // No link/disabled so Material adds no opacity; classes.title applies the primary blue colour
            flat.push({
                id: `cat-${continent.toLowerCase().replace(/\s+/g, '-')}`,
                title: continent,
                type: "basic" as const,
                icon: "",
                classes: { title: 'text-primary font-semibold text-xs uppercase tracking-wider' },
                children: []
            });
            flat.push(...grouped[continent]);
        }
        return flat;
    }

    /**
     * On init
     */
    ngOnInit(): void {
        this.view.isClient = this.tfmService.view.isClient;

        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                if (this.view.isClient) {
                    // Chain getSite() first so isSite11 is always correctly evaluated,
                    // avoiding a race condition where site data is not yet available.
                    this.tfmService.getSite().then((siteRes: any) => {
                        if (!this.view.site) {
                            this.view.site = siteRes;
                            this.view.site.profile = this.tfmService.getSiteProfile(siteRes.profileID);
                        }
                        return this.tfmService.getDestinations();
                    }).then((res: any) => {
                        const grouped: Record<string, any[]> = {};
                        res.forEach((x: any) => {
                            const continent = x.parent?.name || 'Other';
                            if (!grouped[continent]) grouped[continent] = [];
                            grouped[continent].push({
                                id: x.id,
                                title: x.name,
                                type: "basic",
                                icon: "",
                                link: `/destination/${x.id}/${x.seo.alias}`
                            });
                        });
                        const sortedContinents = Object.keys(grouped).sort();
                        const isSite11 = this.view.site?.id == 11;
                        const destinations = isSite11
                            ? this._buildDestinationsFlat(sortedContinents, grouped)
                            : sortedContinents.map(continent => ({
                                id: continent.toLowerCase().replace(/\s+/g, '-'),
                                title: continent,
                                subtitle: "",
                                type: "collapsable" as const,
                                icon: "",
                                children: grouped[continent]
                            }));
                        this.view.pages = res;

                        this.tfmService.getPages().then(pagesRes => {
                            this.view.menuItems = pagesRes;

                            const dynamicNavItems: Navigation['horizontal'] = this.view.menuItems.content
                                .filter(item => item.section !== 'privacy' && item.section !== 'terms' && item.section !== 'specials')
                                .map(item => ({
                                    id: item.name.toLowerCase().replace(/\s+/g, '-'),
                                    title: item.name,
                                    subtitle: "",
                                    type: "basic",
                                    icon: "",
                                    children: [],
                                    link: "/" + item.name.toLowerCase().replace(/\s+/g, '-')
                                }));

                            this.tfmService.getLandingPages().then(lpRes => {
                                this.view.landingPages = lpRes;

                                const specials: Navigation['horizontal'] = (lpRes.content || []).filter(lp => lp.isPinned === true).map((lp: any) => ({
                                    id: lp.id,
                                    title: lp.name,
                                    type: "basic",
                                    icon: "",
                                    link: `/destination/${lp.id}/${lp.seo.alias}`
                                }));
                                const specialsNavItem: Navigation['horizontal'][number] | null = specials.length > 0
                                    ? { id: "specials", title: "Specials", subtitle: "", type: "group", icon: "feather:chevron-down", children: specials }
                                    : null;

                                const newHorizontal: any[] = [];

                                if (this.view.site?.profile?.heading) {
                                    switch (this.view.site.profile.heading.menu) {
                                        case 1:
                                            newHorizontal.push(
                                                { id: "home", title: "Home", subtitle: "", type: "basic", icon: "", children: [], link: "/" },
                                                { id: "destinations", title: "Destinations", subtitle: "", type: "group", icon: "feather:chevron-down", children: destinations },
                                                ...dynamicNavItems,
                                                ...(specialsNavItem ? [specialsNavItem] : [])
                                            );
                                            break;

                                        case 2:
                                            newHorizontal.push(
                                                { id: "home", title: "Home", subtitle: "", type: "basic", icon: "", children: [], link: "/" },
                                                { id: "corporate-travel", title: "Corporate Travel", subtitle: "", type: "basic", icon: "", children: [], link: "corporate-travel" },
                                                { id: "leisure-travel", title: "Leisure Travel", subtitle: "", type: "basic", icon: "", children: [], link: "leisure-travel" },
                                                { id: "branches", title: "Members", subtitle: "", type: "basic", icon: "", children: [], link: "branches" }
                                            );
                                            break;

                                        case 3:
                                            newHorizontal.push(
                                                { id: "home", title: "Home", subtitle: "", type: "basic", icon: "", children: [], link: "/" },
                                                { id: "world-cruises", title: "World Cruises", subtitle: "", type: "basic", icon: "", children: [], link: "/world-cruises" },
                                                { id: "gallery", title: "Gallery", subtitle: "", type: "basic", icon: "", children: [], link: "/gallery" }
                                            );
                                            break;

                                        case 4:
                                            newHorizontal.push(
                                                { id: "home", title: "Home", subtitle: "", type: "basic", icon: "", children: [], link: "/" },
                                                { id: "leisure-travel", title: "Holiday Packages", subtitle: "", type: "basic", icon: "", children: [], link: "leisure-travel/holiday-travel" },
                                                { id: "corporate-travel", title: "Business Travel", subtitle: "", type: "basic", icon: "", children: [], link: "corporate-travel" },
                                                { id: "about", title: "About Us", subtitle: "", type: "basic", icon: "", children: [], link: "/about-us" },
                                                { id: "team", title: "Our Team", subtitle: "", type: "basic", icon: "", children: [], link: "/team" },
                                                { id: "contact", title: "Contact", subtitle: "", type: "basic", icon: "", children: [], link: "/contact-us" },
                                            );
                                            break;
                                    }
                                }

                                // Always assign a new navigation object so Fuse OnPush detects the input change
                                this.navigation = { ...navigation, horizontal: newHorizontal };
                                this._cd.detectChanges();
                            });
                        });
                    });
                } else {
                    this.navigation = navigation;
                }
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
        this.tfmService.getSite().then(res => {
            this.view.site = res;
            this.view.site.profile = this.tfmService.getSiteProfile(this.view.site.profileID);

        });
        this.view.itinGuidChange$ = this.tfmService.itinGuidChange.subscribe(res => {
            this.view.itinGuid = this.tfmService.getLocalItinGuid();

            //this._changeDetectorRef.detectChanges();
        });

        this.view.itinGuid = this.tfmService.getLocalItinGuid();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }
    toggleNavigationB2C(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }
    viewChanged(options) {
        // console.log('options', options)
        this.view.drawerOptions = options;
    }
    quoteCreated(quoteGuid) {
        // console.log("quote", quoteGuid);
        if (quoteGuid)
            this._router.navigate(['/checkout', quoteGuid]);
    }
}
